import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {documentationContactNotesCreate} from 'api/v2';
import {usePrompt} from 'hooks/usePrompt';
import {useStores} from 'hooks/useStores';
import {contactNotesQueryKeys, membersQueryOptions} from 'queries';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {
  ContactMethodSection,
  EncounterSection,
  MemberField,
  NarrativeSection,
  NoteForm,
  useNoteForm,
} from '../NoteForm';
import {CONTACT_METHODS, REASONS, RESPONSES} from './choices';
import {contactNoteDefinition, isNarrativeRequired} from './definition';
import {toRequest} from './payload';
import {BillingSection} from './sections/BillingSection';
import {ChecklistNoteSection} from './sections/ChecklistNoteSection';
import {FollowUpSection} from './sections/FollowUpSection';
import {MemberStatusSection} from './sections/MemberStatusSection';
import {NotReachedSection} from './sections/NotReachedSection';
import {ServiceSection} from './sections/ServiceSection';
import type {ContactNoteAction, ContactNoteState} from './state';

const UNSAVED_CHANGES_MESSAGE =
  'Are you sure you want to leave? You have unsaved changes that will be lost if you navigate away from this page.';

type ContactNoteFormProps = {
  initialMemberId?: string;
};

type ContactNoteFieldsProps = ContactNoteFormProps & {
  onSave: (note: ContactNoteState) => Promise<void>;
};

const ContactNoteFields = ({
  initialMemberId,
  onSave,
}: ContactNoteFieldsProps) => {
  const {state, dispatch, isSubmitting} = useNoteForm<
    ContactNoteState,
    ContactNoteAction
  >();
  const {meStore} = useStores();
  const [initialState] = useState(state);

  // One-shot prefill: a refetch must not overwrite the member the user picked,
  // and a missing member is expected (stale link), so don't retry it
  const prefillQuery = useQuery({
    ...membersQueryOptions.detail(initialMemberId ?? ''),
    enabled: !!initialMemberId,
    staleTime: Infinity,
    retry: false,
  });

  // Notes are only about patients, so a staff id in the link counts as missing
  const prefill =
    prefillQuery.data?.role === 'patient' ? prefillQuery.data : undefined;
  const hasPrefilled = useRef(false);
  useEffect(() => {
    if (prefill && !hasPrefilled.current) {
      hasPrefilled.current = true;
      dispatch({
        type: 'set',
        field: 'member',
        value: {id: prefill.id, name: prefill.name},
      });
    }
  }, [prefill, dispatch]);

  // The prefilled member isn't the user's own change
  const hasChanges = (Object.keys(state) as (keyof ContactNoteState)[]).some(
    (key) =>
      key === 'member'
        ? state.member?.id !== prefill?.id
        : state[key] !== initialState[key],
  );
  usePrompt(UNSAVED_CHANGES_MESSAGE, hasChanges && !isSubmitting);

  return (
    <NoteForm.Layout>
      <NoteForm.Sections>
        <NoteForm.Section id="encounter" title="Encounter">
          <EncounterSection
            memberField={
              <MemberField
                value={state.member}
                isLoading={prefillQuery.isLoading}
                autoFocus={!prefill}
                onChange={(member) =>
                  dispatch({type: 'set', field: 'member', value: member})
                }
              />
            }
            dateOfService={state.dateOfService}
            onDateOfServiceChange={(value) =>
              dispatch({type: 'set', field: 'dateOfService', value})
            }
            staffName={meStore.me?.name ?? ''}
            organizationName={
              meStore.activeMembership?.organization?.name ?? ''
            }
          />
        </NoteForm.Section>
        <NoteForm.Section id="contact" number={1} title="Contact">
          <ContactMethodSection
            options={CONTACT_METHODS}
            value={state.contactMethod}
            onChange={(value) =>
              dispatch({type: 'set', field: 'contactMethod', value})
            }
            detail={state.contactMethodDetail}
            onDetailChange={(value) =>
              dispatch({type: 'set', field: 'contactMethodDetail', value})
            }
          />
        </NoteForm.Section>
        <NoteForm.Section id="notReached" number={2} title="Not reached">
          <NotReachedSection />
        </NoteForm.Section>
        <NoteForm.Section id="service" number={2} title="Service provided">
          <ServiceSection />
        </NoteForm.Section>
        <NoteForm.Section id="reason" number={3} title="Reason">
          <ChecklistNoteSection
            label="Why did you contact the member?"
            options={REASONS}
            field="reasons"
            noteField="objectiveReason"
            noteLabel="Objective reason for contact"
          />
        </NoteForm.Section>
        <NoteForm.Section id="status" number={4} title="Member status">
          <MemberStatusSection />
        </NoteForm.Section>
        <NoteForm.Section id="response" number={5} title="Member response">
          <ChecklistNoteSection
            label="How did the member respond?"
            options={RESPONSES}
            field="responses"
            noteField="responseOutcome"
            noteLabel="Member response / outcome"
          />
        </NoteForm.Section>
        <NoteForm.Section id="followUp" number={6} title="Follow-up">
          <FollowUpSection />
        </NoteForm.Section>
        <NoteForm.Section id="billing" number={7} title="Billing">
          <BillingSection />
        </NoteForm.Section>
        <NoteForm.Section id="narrative" number={8} title="Narrative">
          <NarrativeSection
            narrativeRequired={isNarrativeRequired(state)}
            onSave={() => onSave(state)}
          />
        </NoteForm.Section>
      </NoteForm.Sections>
    </NoteForm.Layout>
  );
};

export const ContactNoteForm = ({initialMemberId}: ContactNoteFormProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Locks the form on the click itself; the mutation reports pending a tick later
  const [isSaving, setIsSaving] = useState(false);
  const mutation = useMutation({
    mutationFn: (note: ContactNoteState) =>
      documentationContactNotesCreate(toRequest(note)),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: contactNotesQueryKeys.lists()});
      // Newest created first, so a backdated note is still on top
      navigate('/documentation/notes?ordering=-created');
    },
  });

  const save = async (note: ContactNoteState) => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      await mutation.mutateAsync(note);
      showAlert('Note saved', ALERT_TYPES.SUCCESS);
    } catch {
      showAlert("Couldn't save the note. Try again.", ALERT_TYPES.ERROR);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <NoteForm definition={contactNoteDefinition} isSubmitting={isSaving}>
      <ContactNoteFields initialMemberId={initialMemberId} onSave={save} />
    </NoteForm>
  );
};
